import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Checkbox } from '@/components_deprecated'
import { RegularModal } from '@/components_deprecated/Modal'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import type { WeekDayValues } from '@/components_deprecated/SelectWeekday/SelectWeekDays'
import { SelectWeekDays } from '@/components_deprecated/SelectWeekday/SelectWeekDays'
import { TimeGraph } from '@/components_deprecated/TimeGraph/TimeGraph'
import { TimePicker } from '@/components_deprecated/TimePicker'
import { format } from 'date-fns'
import { time } from '@/lib/time'
import floor from 'lodash/floor'
import uniqueId from 'lodash/uniqueId'
import { Field, Form } from '@/modules/companies'
import { useMemo } from 'react'
import styled from 'styled-components'
import theme from '@/theme'

const TimePickers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
`

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
`

const TooltipTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
`

const TooltipText = styled.div`
  font-size: 13px;
`

const ErrorWrapper = styled.div`
  color: ${props => props.theme.colors.red};
  display: flex;
  flex-direction: column;
`

export interface PeriodAddonForm {
  id: string
  start: Date
  end: Date
  salaryCode: string | null
  excludeIfOvertime: boolean
  override: boolean

  salaryCodeName: string

  alwaysActive: boolean

  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

interface PeriodPromptProps {
  period: PeriodAddonForm | null
  existingPeriods: PeriodAddonForm[]
  isAddon: boolean

  onSubmit: (period: PeriodAddonForm | null) => void
}

export const PeriodPrompt: React.FC<PeriodPromptProps> = ({
  period,
  existingPeriods,
  isAddon,

  onSubmit,
}) => {
  const translations = useTranslate({
    createOvertimePeriod: 'companies.create-overtime-period',
    editOvertimePeriod: 'companies.edit-overtime-period',
    createPeriodAddon: 'companies.create-period-addon',
    editPeriodAddon: 'companies.edit-period-addon',
    save: 'common.save',
    timeInterval: 'common.time-interval',
    selectWeekdays: 'common.select-week-days',
    overtimeRate: 'common.overtime-rate',
    startBeforeEnd: 'timesheets.error.start-before-end',
    requied: 'common.required',
    existingOvertimePeriod: 'companies.existing-overtime-period',
    salaryCode: 'settings.salary-code',
    overlappingPeriodsAreNotAllowed:
      'companies.overlapping-periods-are-not-allowed',
    excludeIfOvertime: 'settings.exclude-if-overtime',
    overrideOtherAddons: 'salary.override-other-addons',
    alwaysActive: 'settings.period-overtime-always-active',
  })

  const {
    formValues,
    formValid,
    updateForm,
    formChangeHandler: handler,
    formErrors: errors,
  } = useForm<PeriodAddonForm>({
    values: {
      id: uniqueId('overtime-period-'),
      start: time(period?.start ?? '18:00').toDate(new Date()),
      end: time(period?.end ?? '24:00').toDate(new Date()),
      salaryCode: period?.salaryCode ?? null,
      salaryCodeName: period?.salaryCodeName ?? '',
      excludeIfOvertime:
        (period as PeriodAddonForm)?.excludeIfOvertime ?? false,
      override: (period as PeriodAddonForm)?.override ?? false,
      alwaysActive: (period as PeriodAddonForm)?.alwaysActive ?? false,

      monday: period?.monday ?? false,
      tuesday: period?.tuesday ?? false,
      wednesday: period?.wednesday ?? false,
      thursday: period?.thursday ?? false,
      friday: period?.friday ?? false,
      saturday: period?.saturday ?? false,
      sunday: period?.sunday ?? false,
    },
    validators: {
      start: (value, values) => {
        if (!value) return translations.requied
        if (values.end.getHours() === 0 && values.end.getMinutes() === 0)
          return null
        if (
          (value.getHours() === values.end.getHours() &&
            value.getMinutes() === values.end.getMinutes()) ||
          values.end < value
        )
          return translations.startBeforeEnd
        return null
      },
      end: (value, values) => {
        if (!value) return translations.requied
        if (value.getHours() === 0 && value.getMinutes() === 0) return null
        if (
          (value.getHours() === values.start.getHours() &&
            value.getMinutes() === values.start.getMinutes()) ||
          value < values.start
        )
          return translations.startBeforeEnd
        return null
      },
      monday: (value, values) => {
        if (
          !value &&
          !values.tuesday &&
          !values.wednesday &&
          !values.thursday &&
          !values.friday &&
          !values.saturday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      tuesday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.wednesday &&
          !values.thursday &&
          !values.friday &&
          !values.saturday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      wednesday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.tuesday &&
          !values.thursday &&
          !values.friday &&
          !values.saturday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      thursday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.tuesday &&
          !values.wednesday &&
          !values.friday &&
          !values.saturday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      friday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.tuesday &&
          !values.wednesday &&
          !values.thursday &&
          !values.saturday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      saturday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.tuesday &&
          !values.wednesday &&
          !values.thursday &&
          !values.friday &&
          !values.sunday
        )
          return translations.requied
        return null
      },
      sunday: (value, values) => {
        if (
          !value &&
          !values.monday &&
          !values.tuesday &&
          !values.wednesday &&
          !values.thursday &&
          !values.friday &&
          !values.saturday
        )
          return translations.requied
        return null
      },
    },
    config: {
      validateImmediately: true,
    },
  })

  const relevantOvertimePeriods = existingPeriods.filter(
    existingPeriod =>
      existingPeriod.id !== period?.id &&
      ((formValues.monday && existingPeriod.monday === formValues.monday) ||
        (formValues.tuesday && existingPeriod.tuesday === formValues.tuesday) ||
        (formValues.wednesday &&
          existingPeriod.wednesday === formValues.wednesday) ||
        (formValues.thursday &&
          existingPeriod.thursday === formValues.thursday) ||
        (formValues.friday && existingPeriod.friday === formValues.friday) ||
        (formValues.saturday &&
          existingPeriod.saturday === formValues.saturday) ||
        (formValues.sunday && existingPeriod.sunday === formValues.sunday))
  )

  const handleDaySelect = (dayIndex: number, selected: boolean) => {
    if (dayIndex === 0) {
      updateForm({ monday: selected })
    } else if (dayIndex === 1) {
      updateForm({ tuesday: selected })
    } else if (dayIndex === 2) {
      updateForm({ wednesday: selected })
    } else if (dayIndex === 3) {
      updateForm({ thursday: selected })
    } else if (dayIndex === 4) {
      updateForm({ friday: selected })
    } else if (dayIndex === 5) {
      updateForm({ saturday: selected })
    } else if (dayIndex === 6) {
      updateForm({ sunday: selected })
    }
  }

  function handleSubmit() {
    if (!formValid) return
    onSubmit({
      ...formValues,
    })
  }

  const periodAddonGraphStartInMinutes = time(formValues.start).reduce(
    'minutes'
  )

  const periodAddonGraphEndInMinutes =
    time(formValues.end).reduce('minutes') === 0
      ? 24 * 60
      : time(formValues.end).reduce('minutes')

  const periodAddonGraph = {
    start: periodAddonGraphStartInMinutes,
    end: periodAddonGraphEndInMinutes,
    color: theme.colors.secondary,
    borderColor: '#00ABA7',
  }

  const weekDayValues = useMemo<WeekDayValues>(
    () => [
      formValues.monday,
      formValues.tuesday,
      formValues.wednesday,
      formValues.thursday,
      formValues.friday,
      formValues.saturday,
      formValues.sunday,
    ],
    [formValues]
  )

  const weekDayError =
    errors.monday ||
    errors.tuesday ||
    errors.wednesday ||
    errors.thursday ||
    errors.friday ||
    errors.saturday ||
    errors.sunday

  const relevantOvertimePeriodGraphs = relevantOvertimePeriods.map(
    periodAddon => {
      const start = time(periodAddon.start).reduce('minutes')
      const end =
        time(periodAddon.end).reduce('minutes') === 0
          ? 24 * 60
          : time(periodAddon.end).reduce('minutes')
      return {
        start: start,
        end: end,
        color: theme.colors.gray6,
        borderColor: '#9399BB',
        tooltip: (
          <Tooltip>
            <TooltipTitle>{translations.existingOvertimePeriod}</TooltipTitle>
            <TooltipText>
              {format(periodAddon.start, 'HH:mm')} -{' '}
              {format(periodAddon.end, 'HH:mm')}
            </TooltipText>
          </Tooltip>
        ),
      }
    }
  )

  const overlappingPeriods = relevantOvertimePeriodGraphs.filter(
    relevantOvertimePeriodGraph => {
      const start = relevantOvertimePeriodGraph.start
      const end = relevantOvertimePeriodGraph.end

      return (
        (start >= periodAddonGraphStartInMinutes &&
          start < periodAddonGraphEndInMinutes) ||
        (end > periodAddonGraphStartInMinutes &&
          end <= periodAddonGraphEndInMinutes) ||
        (start <= periodAddonGraphStartInMinutes &&
          end >= periodAddonGraphEndInMinutes)
      )
    }
  )

  const hasOverlappingPeriods = overlappingPeriods.length > 0

  const createTitle = translations.createPeriodAddon
  const editTitle = translations.editPeriodAddon

  return (
    <RegularModal
      title={period ? editTitle : createTitle}
      width="488px"
      submitText={translations.save}
      submitDisabled={!formValid || hasOverlappingPeriods}
      onCancel={() => onSubmit(null)}
      onSubmit={() => handleSubmit()}
    >
      <Form>
        <Field>
          <label>{translations.salaryCode}</label>
          <SalaryCodeSelect
            fullWidth
            nullable
            value={formValues.salaryCode}
            onChange={(value, option) => {
              updateForm({
                salaryCodeName: option?.extra?.name ?? '',
                salaryCode: value,
              })
            }}
          />
        </Field>
        {isAddon && (
          <>
            <Field alignLeft>
              <label>{translations.overrideOtherAddons}</label>
              <Checkbox
                checked={formValues.override}
                onChange={handler('override')}
              />
            </Field>

            <Field alignLeft>
              <label>{translations.excludeIfOvertime}</label>
              <Checkbox
                checked={formValues.excludeIfOvertime}
                onChange={handler('excludeIfOvertime')}
              />
            </Field>
          </>
        )}

        {!isAddon && (
          <Field alignLeft>
            <label>{translations.alwaysActive}</label>
            <Checkbox
              checked={formValues.alwaysActive}
              onChange={handler('alwaysActive')}
            />
          </Field>
        )}

        <Field>
          <label>{translations.selectWeekdays}</label>
          <SelectWeekDays
            initialValues={weekDayValues}
            onSelectDay={handleDaySelect}
            error={weekDayError}
          />
        </Field>

        <Field>
          <label>{translations.timeInterval}</label>
          <TimePickers>
            <TimePicker
              width="100%"
              disableWheel
              value={formValues.start}
              onChange={handler('start')}
            />
            <TimePicker
              width="100%"
              disableWheel
              value={formValues.end}
              onChange={handler('end')}
              error={errors.end ?? errors.start}
            />
          </TimePickers>
        </Field>

        <Field>
          <TimeGraph
            includeHourMarks
            graphs={[periodAddonGraph].concat(relevantOvertimePeriodGraphs)}
          />
        </Field>

        {hasOverlappingPeriods && (
          <Field>
            <ErrorWrapper>
              {translations.overlappingPeriodsAreNotAllowed}:
              {overlappingPeriods.map((period, idx) => (
                <p key={idx}>
                  {floor(period.start / 60)}:
                  {String(period.start % 60).padStart(2, '0')} -{' '}
                  {floor(period.end / 60)}:
                  {String(period.end % 60).padStart(2, '0')}
                </p>
              ))}
            </ErrorWrapper>
          </Field>
        )}
      </Form>
    </RegularModal>
  )
}
